// What the Kézbesítés screen makes of its data. No React here, so
// tests/admin can run every decision on Node: which rows a range shows and
// what they add up to, what a day's rates are against rule 4, what the
// sparklines draw, and what goes on the "mi piros most" strip.

import type { AlertRow, ReputationRow } from "@/lib/api";
import type { DnsblIp, LivePoolDay, PoolMeta, WatchSource } from "@/lib/tracking";
import { addDays, daysFrom } from "@/components/emc/days";
import type { Tone } from "@/components/emc/status";
import { labelOf, statusLook } from "@/components/emc/status";
import { POOL_MIN_SAMPLE, PROVIDER_NOTE, judge, verdictText } from "@/components/emc/thresholds";
import type { RateVerdict, Rule4Metric } from "@/components/emc/thresholds";

/** `emc_db.isp.ISPS`, the order the tables use. */
export const ISP_ORDER = ["gmail", "microsoft", "yahoo", "apple", "local", "other"] as const;

/** DESIGN.md "Szikra": eight measurements. */
export const SPARK_DAYS = 8;

export interface Figures {
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  delayed: number;
  rejected: number;
  unsubscribed: number;
}

const FIELDS: (keyof Figures)[] = [
  "sent",
  "delivered",
  "bounced",
  "complained",
  "delayed",
  "rejected",
  "unsubscribed",
];

export function zero(): Figures {
  return { sent: 0, delivered: 0, bounced: 0, complained: 0, delayed: 0, rejected: 0, unsubscribed: 0 };
}

function add(into: Figures, from: Partial<Figures>): void {
  for (const field of FIELDS) into[field] += from[field] ?? 0;
}

function any(figures: Figures): boolean {
  return FIELDS.some((field) => figures[field] > 0);
}

/** The days to fetch: the range, and the sparklines' eight days before its end. */
export function fetchWindow(range: { from: string; to: string }): { from: string; to: string } {
  const sparkFrom = addDays(range.to, -(SPARK_DAYS - 1));
  return { from: sparkFrom < range.from ? sparkFrom : range.from, to: range.to };
}

// --- the range table --------------------------------------------------------------

export interface RateCells {
  figures: Figures;
  bounce: RateVerdict;
  complaint: RateVerdict;
}

/** The rule's two rates for a row. A pool's day and one provider's row are
 * held to the same sample: under it the rule decides nothing. */
function cells(figures: Figures): RateCells {
  return {
    figures,
    bounce: judge("bounce", figures.bounced, figures.sent, POOL_MIN_SAMPLE),
    complaint: judge("complaint", figures.complained, figures.sent, POOL_MIN_SAMPLE),
  };
}

/** A pool row's rate over the range, coloured no milder than its worst day.
 *
 * The number and the zone are the range's sum; rule 4 judges days, so the
 * tone is the worse of the sum's and the worst single day's, and `note` says
 * which day that was. A week of 7000 sends with four complaints on one day is
 * 0,06 % summed and grey, while that day was 0,4 % and stopped the pool. */
export interface RangeVerdict extends RateVerdict {
  /** The day with the worst verdict in the range, when one crossed a line. */
  worstDay: { day: string; verdict: RateVerdict } | null;
  /** "a legrosszabb nap: …", for the hover; null when no day crossed a line. */
  note: string | null;
}

/** Only the rule's two colours rank: brick over amber over everything else. */
function toneRank(tone: Tone): number {
  return tone === "danger" ? 2 : tone === "warn" ? 1 : 0;
}

/** The sum's verdict, escalated to the worst day the range holds. A day under
 * the sample is no verdict (the rule decides nothing on it), so it colours
 * nothing here either. */
function worstOfDays(sum: RateVerdict, days: { day: string; figures: Figures }[]): RangeVerdict {
  let worst: { day: string; verdict: RateVerdict } | null = null;
  for (const { day, figures } of days) {
    const part = sum.metric === "bounce" ? figures.bounced : figures.complained;
    const verdict = judge(sum.metric, part, figures.sent, POOL_MIN_SAMPLE);
    if (toneRank(verdict.tone) === 0) continue;
    if (
      worst === null ||
      toneRank(verdict.tone) > toneRank(worst.verdict.tone) ||
      (toneRank(verdict.tone) === toneRank(worst.verdict.tone) &&
        part * worst.verdict.sent > worst.verdict.part * figures.sent)
    ) {
      worst = { day, verdict };
    }
  }
  if (worst === null) return { ...sum, worstDay: null, note: null };
  const tone = toneRank(worst.verdict.tone) > toneRank(sum.tone) ? worst.verdict.tone : sum.tone;
  return {
    ...sum,
    tone,
    worstDay: worst,
    note: `a legrosszabb nap: ${worst.day} — ${verdictText(worst.verdict, POOL_MIN_SAMPLE)}`,
  };
}

export interface Series {
  /** Oldest first, SPARK_DAYS long, in percent; null where nothing was sent. */
  values: (number | null)[];
  /** The end dot: the rule's colour for the last day that sent, pine otherwise. */
  tone: Tone;
}

export interface RangeRow extends RateCells {
  key: string;
  pool: string;
  /** null for the pool's own row, over every provider. */
  isp: string | null;
  /** The pool row's are no milder than its worst day; a provider's row is a
   * signal, not the rule's unit, and carries no worst day. */
  bounce: RangeVerdict;
  complaint: RangeVerdict;
  bounceSeries: Series;
  complaintSeries: Series;
}

function dayIndex(days: LivePoolDay[]): Map<string, LivePoolDay> {
  return new Map(days.map((row) => [`${row.pool}|${row.day}`, row]));
}

/** The two counts rule 4 reads, the worse of the recount and the counters.
 *
 * The scheduler decides on `feedback.processor.rates()`: the pool's sends over
 * its `pool_daily_stats` counters, while the recount reads `delivery_event`.
 * They count the same events — a feedback-loop complaint (an ARF report
 * through the inbound door) writes a `complaint` event of its own since the
 * UI-7 second look — but an event or an increment can still be lost on one
 * side. A pool's day is judged on the larger of each, so the screen is never
 * milder than the pause, and `disagreement` says which count differs. */
const JUDGED: ("bounced" | "complained")[] = ["bounced", "complained"];

function figuresOf(row: LivePoolDay | undefined, isp: string | null): Figures {
  const figures = zero();
  if (row === undefined) return figures;
  if (isp === null) {
    add(figures, row.totals);
    for (const field of JUDGED) {
      figures[field] = Math.max(figures[field], row.counters?.[field] ?? 0);
    }
  } else {
    add(figures, row.by_isp.find((item) => item.isp === isp) ?? {});
  }
  return figures;
}

function series(
  index: Map<string, LivePoolDay>,
  pool: string,
  isp: string | null,
  metric: Rule4Metric,
  end: string,
): Series {
  let tone: Tone = "running";
  const values = daysFrom(addDays(end, -(SPARK_DAYS - 1)), end).map((day) => {
    const figures = figuresOf(index.get(`${pool}|${day}`), isp);
    if (figures.sent <= 0) return null;
    const part = metric === "bounce" ? figures.bounced : figures.complained;
    const verdict = judge(metric, part, figures.sent, POOL_MIN_SAMPLE);
    tone = verdict.tone === "muted" ? "running" : verdict.tone;
    return (part / figures.sent) * 100;
  });
  return { values, tone };
}

/** Per pool, the range summed, then its providers under it. A pool or a
 * provider with nothing at all in the range is left out; a row with events
 * but no sends stays (a late bounce is news) and its rates are "—". A pool's
 * row is coloured no milder than its worst day (`RangeVerdict`). */
export function rangeTable(days: LivePoolDay[], range: { from: string; to: string }, poolOrder: string[]): RangeRow[] {
  const index = dayIndex(days);
  const inRange = days.filter((row) => row.day >= range.from && row.day <= range.to);
  const pools = [...new Set([...poolOrder, ...inRange.map((row) => row.pool)])];
  const rows: RangeRow[] = [];
  for (const pool of pools) {
    const mine = inRange.filter((row) => row.pool === pool);
    const total = zero();
    const daily = mine.map((row) => ({ day: row.day, figures: figuresOf(row, null) }));
    for (const { figures } of daily) add(total, figures);
    if (!any(total)) continue;
    const summed = cells(total);
    rows.push({
      key: pool,
      pool,
      isp: null,
      figures: total,
      bounce: worstOfDays(summed.bounce, daily),
      complaint: worstOfDays(summed.complaint, daily),
      bounceSeries: series(index, pool, null, "bounce", range.to),
      complaintSeries: series(index, pool, null, "complaint", range.to),
    });
    const known = new Set<string>(ISP_ORDER);
    const extra = [...new Set(mine.flatMap((row) => row.by_isp.map((item) => item.isp)))].filter(
      (isp) => !known.has(isp),
    );
    for (const isp of [...ISP_ORDER, ...extra]) {
      const figures = zero();
      for (const row of mine) add(figures, figuresOf(row, isp));
      if (!any(figures)) continue;
      const summed = cells(figures);
      rows.push({
        key: `${pool}/${isp}`,
        pool,
        isp,
        figures,
        bounce: { ...summed.bounce, worstDay: null, note: null },
        complaint: { ...summed.complaint, worstDay: null, note: null },
        bounceSeries: series(index, pool, isp, "bounce", range.to),
        complaintSeries: series(index, pool, isp, "complaint", range.to),
      });
    }
  }
  return rows;
}

// --- day by day -------------------------------------------------------------------

export interface DayRow extends RateCells {
  key: string;
  pool: string;
  day: string;
  live: boolean;
  recomputed_at: string | null;
  /** What the feedback worker counted differs from the recount. */
  disagreement: { field: string; counted: number; recomputed: number }[];
  source: LivePoolDay;
}

const COUNTED: (keyof Figures)[] = ["delivered", "bounced", "complained", "delayed", "rejected"];

/** One row per pool per day, newest first — the unit rule 4 judges. */
export function dayRows(days: LivePoolDay[], range: { from: string; to: string }): DayRow[] {
  return days
    .filter((row) => row.day >= range.from && row.day <= range.to)
    .filter((row) => any(figuresOf(row, null)) || COUNTED.some((field) => (row.counters[field] ?? 0) > 0))
    .map((row) => ({
      key: `${row.day}/${row.pool}`,
      pool: row.pool,
      day: row.day,
      live: row.live,
      recomputed_at: row.recomputed_at,
      ...cells(figuresOf(row, null)),
      disagreement: COUNTED.filter((field) => (row.counters[field] ?? 0) !== row.totals[field]).map((field) => ({
        field,
        counted: row.counters[field] ?? 0,
        recomputed: row.totals[field],
      })),
      source: row,
    }))
    .sort((left, right) => (left.day === right.day ? left.pool.localeCompare(right.pool) : left.day < right.day ? 1 : -1));
}

// --- what is red now ----------------------------------------------------------------

export interface RedItem {
  key: string;
  tone: Tone;
  /** The state, in a word, for the pill: "megállítva", "küszöb fölött". */
  label: string;
  /** Worst first on the strip. */
  severity: number;
  /** The pool it is about, linked to its warmup card. */
  pool: string | null;
  /** What else it is about: a provider, an address, a domain, a source. */
  subject: string;
  /** "ma eddig", "tegnap" — when a figure; empty when a state. */
  when: string;
  text: string;
  /** Where to act on it, beyond the pool's warmup card. */
  action: { to: string; label: string } | null;
}

export interface RedInput {
  today: string;
  days: LivePoolDay[];
  pools: PoolMeta[];
  dnsbl: DnsblIp[];
  reputation: ReputationRow[];
  alerts: AlertRow[];
  sources: WatchSource[];
}

/** The sources whose silence matters on this screen. */
export const WATCHED_SOURCES = ["postmaster", "snds", "dnsbl", "dmarc", "ses"] as const;

/** The pill's word for where a rate stands. */
export function zoneWord(verdict: RateVerdict): string {
  if (verdict.small) return "kevés adat";
  if (verdict.zone === "over") return "küszöb fölött";
  if (verdict.zone === "between") return "emelés tartva";
  if (verdict.near) return "közel";
  return statusLook("ruleZone", verdict.zone).label;
}

function percentOf(factor: number): string {
  return `${Math.round(factor * 100)}%`;
}

// The strip's order, worst first, as data rather than arithmetic. A stopped
// pool and a listed address lead; a pool over a line comes before one of its
// providers over it; a source that has gone quiet — blindness — before a pool
// that is merely close to a line.
export const STRIP_WEIGHT = {
  paused: 20,
  listed: 18,
  reputationBad: 16,
  throttled: 11,
  reputationWarn: 9,
  poolOver: 8,
  providerOver: 7,
  poolBetween: 6,
  providerBetween: 5,
  criticalAlerts: 5,
  sourceFailed: 4,
  poolNear: 3,
  poolSmallOver: 2,
} as const;

function poolWeight(verdict: RateVerdict): number {
  if (verdict.small) return verdict.zone === "over" ? STRIP_WEIGHT.poolSmallOver : 0;
  if (verdict.zone === "over") return STRIP_WEIGHT.poolOver;
  if (verdict.zone === "between") return STRIP_WEIGHT.poolBetween;
  return verdict.near ? STRIP_WEIGHT.poolNear : 0;
}

function rateItems(input: RedInput, day: string, when: string): RedItem[] {
  const items: RedItem[] = [];
  for (const row of input.days.filter((item) => item.day === day)) {
    const pool = cells(figuresOf(row, null));
    for (const verdict of [pool.bounce, pool.complaint]) {
      const weight = poolWeight(verdict);
      if (weight === 0) continue;
      items.push({
        key: `${row.pool}|${day}|${verdict.metric}`,
        // Near and too-small are sentences; only the rule's zones colour.
        tone: verdict.tone,
        label: zoneWord(verdict),
        severity: weight,
        pool: row.pool,
        subject: "",
        when,
        text: verdictText(verdict, POOL_MIN_SAMPLE),
        action: null,
      });
    }
    for (const isp of ISP_ORDER) {
      const provider = cells(figuresOf(row, isp));
      for (const verdict of [provider.bounce, provider.complaint]) {
        if (verdict.small || (verdict.zone !== "over" && verdict.zone !== "between")) continue;
        items.push({
          key: `${row.pool}|${day}|${isp}|${verdict.metric}`,
          tone: verdict.tone,
          label: zoneWord(verdict),
          severity: verdict.zone === "over" ? STRIP_WEIGHT.providerOver : STRIP_WEIGHT.providerBetween,
          pool: row.pool,
          subject: labelOf("isp", isp),
          when,
          text: `${verdictText(verdict, POOL_MIN_SAMPLE)}; ${PROVIDER_NOTE}`,
          action: null,
        });
      }
    }
  }
  return items;
}

function latestReputation(rows: ReputationRow[]): ReputationRow[] {
  const latest = new Map<string, ReputationRow>();
  for (const row of rows) {
    const key = `${row.source}|${row.subject}`;
    const seen = latest.get(key);
    if (seen === undefined || seen.day < row.day) latest.set(key, row);
  }
  return [...latest.values()];
}

export function redNow(input: RedInput): RedItem[] {
  const items: RedItem[] = [];

  for (const pool of input.pools) {
    if (pool.is_paused || pool.watch_state === "paused") {
      items.push({
        key: `${pool.pool}|paused`,
        tone: "danger",
        label: "megállítva",
        severity: STRIP_WEIGHT.paused,
        pool: pool.pool,
        subject: "",
        when: "",
        text: pool.paused_reason || "a pool áll; az ok az Őrszem naplójában",
        action: { to: `/orszem?pool=${encodeURIComponent(pool.pool)}`, label: "Őrszem" },
      });
    } else if (pool.watch_state === "throttled" || pool.throttle_factor < 1) {
      items.push({
        key: `${pool.pool}|throttled`,
        tone: "warn",
        label: "lassítva",
        severity: STRIP_WEIGHT.throttled,
        pool: pool.pool,
        subject: "",
        when: "",
        text: `a napi keretből csak ${percentOf(pool.throttle_factor)} megy ki`,
        action: { to: `/orszem?pool=${encodeURIComponent(pool.pool)}`, label: "Őrszem" },
      });
    }
  }

  items.push(...rateItems(input, input.today, "ma eddig"));
  items.push(...rateItems(input, addDays(input.today, -1), "tegnap"));

  for (const address of input.dnsbl) {
    if (address.level !== "bad") continue;
    items.push({
      key: `dnsbl|${address.ip}`,
      tone: "danger",
      label: "listán",
      severity: STRIP_WEIGHT.listed,
      pool: null,
      subject: address.ip,
      when: "",
      text: `feketelista: ${address.listed_on.join(", ") || address.value}`,
      action: { to: "/orszem?source=dnsbl", label: "Őrszem" },
    });
  }

  for (const row of latestReputation(input.reputation)) {
    const verdicts: { word: string; tone: Tone; label: string }[] = [];
    const domain = row.metrics.domain_reputation;
    if (row.source === "postmaster" && typeof domain === "string") {
      const look = statusLook("postmasterReputation", domain);
      if (look.tone !== "muted") {
        verdicts.push({ word: "Postmaster domain-reputáció", tone: look.tone, label: look.label });
      }
    }
    const filter = row.metrics.filter_result;
    if (row.source === "snds" && typeof filter === "string") {
      const look = statusLook("sndsFilter", filter);
      if (look.tone !== "muted") {
        verdicts.push({ word: "SNDS szűrési eredmény", tone: look.tone, label: look.label });
      }
    }
    for (const verdict of verdicts) {
      items.push({
        key: `rep|${row.source}|${row.subject}|${verdict.word}`,
        tone: verdict.tone,
        label: verdict.label,
        severity: verdict.tone === "danger" ? STRIP_WEIGHT.reputationBad : STRIP_WEIGHT.reputationWarn,
        pool: null,
        subject: row.subject,
        when: row.day,
        text: verdict.word,
        action: { to: `/orszem?source=${row.source}`, label: "Őrszem" },
      });
    }
  }

  const critical = input.alerts.filter((alert) => alert.level === "critical" && alert.acknowledged_at === null);
  if (critical.length > 0) {
    items.push({
      key: "alerts|critical",
      tone: "danger",
      label: "kritikus",
      severity: STRIP_WEIGHT.criticalAlerts,
      pool: null,
      subject: "",
      when: "",
      text: `${critical.length} nyugtázatlan kritikus riasztás`,
      action: { to: "#riasztasok", label: "Riasztások" },
    });
  }

  for (const run of input.sources) {
    if (run.last_status !== "failed" || !(WATCHED_SOURCES as readonly string[]).includes(run.source)) continue;
    items.push({
      key: `source|${run.source}`,
      tone: "warn",
      label: "hiba",
      severity: STRIP_WEIGHT.sourceFailed,
      pool: null,
      subject: labelOf("watchSource", run.source),
      when: "",
      text: "a legutóbbi lekérdezés hibával ért véget — amíg így marad, erről a forrásról nincs friss jel",
      action: { to: `/orszem?source=${run.source}`, label: "Őrszem" },
    });
  }

  return items.sort((left, right) => right.severity - left.severity || left.key.localeCompare(right.key));
}

// --- the evidence (slice 5.4) -----------------------------------------------------

/** `emc_db.bounce_codes.DiagnosticClass`, in Hungarian. tests/admin holds the
 * two lists to each other, so a class added there is a label missing here. */
export const DIAGNOSTIC_CLASS_LABEL: Record<string, string> = {
  isp_block: "blokk (lista, IP-tiltás)",
  reputation: "hírnév, tempó",
  policy: "szabályzat",
  content: "tartalom",
  auth_failure: "hitelesítés (SPF/DKIM/DMARC)",
  mailbox_full: "tele a postafiók",
  mailbox_unavailable: "nincs ilyen postafiók",
  relay_denied: "a fogadó nem kezeli a domaint (relay)",
  unknown: "nem besorolható",
};

export function classLabel(name: string): string {
  return DIAGNOSTIC_CLASS_LABEL[name] ?? name;
}

/** "54.240.8.70", the SES host while its letter is unmapped, or "ismeretlen". */
export function senderLabel(sendingIp: string): string {
  return sendingIp === "" ? "ismeretlen" : sendingIp;
}

/** The largest classes of a split, in words: "blokk 12 · tempó 3". */
export function classSplit(byClass: Record<string, number>, shown = 2): string {
  const entries = Object.entries(byClass)
    .filter(([, count]) => count > 0)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]));
  const head = entries.slice(0, shown).map(([name, count]) => `${classLabel(name)} ${count}`);
  const rest = entries.slice(shown).reduce((sum, [, count]) => sum + count, 0);
  return rest > 0 ? [...head, `egyéb ${rest}`].join(" · ") : head.join(" · ");
}
